/* Presence gives way first when the bar runs out of room, so Perform and More are never pushed out */
.presence {
  margin: 0 6px 0 4px;
  min-width: 0;
  overflow: hidden;
}

.menuButton {
  min-width: 32px;
  padding-right: 8px;
  flex-shrink: 0;
}

.breadcrumbs {
  /* Fill the free space: the breadcrumbs measure their own width to decide how many crumbs fit.
   * Shrunk to content, a collapsed path measures too narrow to ever expand. The favorite star
   * renders inside them, right after the last crumb. */
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

/* Small-screen title beside the path button: takes the free space, one line, truncated */
.title {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: var(--tuna-typography-fontSize-md);
  font-weight: var(--tuna-typography-fontWeight-bold);
  color: var(--tuna-palette-text-primary);
}

.agents {
  display: inline-flex;
  flex-shrink: 0;
}

/* The pill renders nothing without agents: drop the empty slot and its hairline too */
.agents:empty,
.agents:empty + .divider {
  display: none;
}

/* Hairline between the Agents pill and the page controls */
.divider {
  width: 1px;
  height: 20px;
  margin: 0 6px;
  background-color: var(--tuna-palette-divider);
  flex-shrink: 0;
}

/* The Agents pill reads as one of the bar's ghost controls: no outline, 32px, the shared radius */
.agents button {
  height: 32px;
  border-color: transparent;
  border-radius: var(--tuna-shape-radius-md);
  background-color: transparent;
  color: var(--tuna-content-secondary);
}

.agents button:hover {
  background-color: var(--tuna-palette-action-hover);
  color: var(--tuna-content-primary);
}

/* Sentence case: "Main", "Share", "Perform" read as words. `button` outranks the Button primitive's uppercase. */
button.layoutSelector,
button.textButton,
button.performButton {
  text-transform: none;
  flex-shrink: 0;
  height: 32px;
  min-width: 0;
  padding: 0 10px;
  border-radius: var(--tuna-shape-radius-md);
  font-size: var(--tuna-typography-fontSize-sm);
  font-weight: 500;
  letter-spacing: normal;
}

/* Ghost controls: secondary text at rest, the shared hover */
button.layoutSelector,
button.textButton,
button.moreButton {
  color: var(--tuna-content-secondary);
  background-color: transparent;
}

button.layoutSelector:hover,
button.textButton:hover,
button.moreButton:hover {
  background-color: var(--tuna-palette-action-hover);
  color: var(--tuna-content-primary);
}

/* More: a 32px ghost icon button */
button.moreButton {
  width: 32px;
  height: 32px;
  min-width: 32px;
  padding: 0;
  border-radius: var(--tuna-shape-radius-md);
  flex-shrink: 0;
}

/* The one filled button: flat, like the rest of the chrome */
button.performButton {
  padding: 0 12px 0 10px;
  font-weight: 550;
  box-shadow: none;
}

button.performButton:hover {
  box-shadow: none;
}

/* Its focus ring sits outside, with a contrast gap against the primary fill */
button.performButton.performButton:focus-visible {
  outline: 2px solid var(--tuna-focus-ring);
  outline-offset: 2px;
  box-shadow: 0 0 0 2px var(--tuna-palette-primary-contrastText);
}

@media (pointer: coarse) {
  button.layoutSelector,
  button.textButton,
  button.performButton,
  .agents button {
    height: 44px;
  }

  button.moreButton {
    width: 44px;
    height: 44px;
  }
}

/*
 * Mobile Edit toggle: a text toggle in the bar, pressed while editing. The two states have to read
 * apart at a glance: off is outlined, on is filled (the default toggle's 12% tint vs grey did not).
 * The label stays "Edit" in both, so the name "Enable edit mode" keeps containing it (WCAG 2.5.3);
 * aria-pressed carries the state.
 */
button.editToggle {
  text-transform: none;
  flex-shrink: 0;
  padding: 0 var(--tuna-spacing-1);
  border: 1px solid var(--tuna-palette-primary-main);
  color: var(--tuna-palette-primary-main);
  background: transparent;
}

button.editToggle[aria-pressed='true'],
button.editToggle[aria-pressed='true']:hover {
  background: var(--tuna-palette-primary-main);
  color: var(--tuna-content-onAccent);
}

/* Compact Perform on small screens: a filled square icon button */
button.performCompact {
  min-width: 36px;
  padding: 6px;
}

/*
 * Small-screen touch targets: at least 44 × 44. The boxed Edit and Perform keep their look and extend an
 * invisible hit area instead (a few px into the 8px toolbar gap, so neighbours never overlap). The More
 * trigger has no visible box, so it simply grows.
 */
button.editToggle,
button.performCompact,
.compactBar .agents button {
  position: relative;
}

button.editToggle::after,
button.performCompact::after,
.compactBar .agents button::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: max(100%, 44px);
  height: max(100%, 44px);
  transform: translate(-50%, -50%);
}

button.moreCompact {
  min-width: 44px;
  min-height: 44px;
  padding: 0;
}
